import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { filter, startWith, takeUntil } from 'rxjs/operators';

export interface Option {
  value: any;
  viewValue: string;
  viewValueTwo?: string;
  viewValueThree?: string;
  selected?: boolean;
  isWrite?: boolean;
  isRead?: boolean;
  isShow?: boolean;
}

@Component({
  selector: 'vi-dropdown-autocomplete',
  templateUrl: 'dropdown-autocomplete.component.html',
  styleUrls: ['dropdown-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViDropdownAutocompleteComponent implements OnChanges, OnInit, OnDestroy {
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  formControl = new FormControl<string | undefined>(undefined);
  autocompleteValues: Option[];
  autocompleteHeight = '256px';

  @Input() placeholder = '';
  @Input() options: Option[];
  @Input() width = '100%';
  @Input() disabled = false;
  @Input() elementHeight: number = 25;

  @Output() optionChange: EventEmitter<Option> = new EventEmitter<Option>();

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('input') input: ElementRef;
  @ViewChild('input', { read: MatAutocompleteTrigger })
  autoCompleteTrigger: MatAutocompleteTrigger;

  ngOnInit(): void {
    window.addEventListener('scroll', this.scrollEvent, true);
    this.formControl.valueChanges
      .pipe(
        filter((filterText) => typeof filterText === 'string'), // to filter out nullish value and avoid rerendering
        startWith(''), // necessary to init autocomplete on component init
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((filterText: string) => {
        this.setAutocompleteValues(filterText);
        this.setHeight();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    window.removeEventListener('scroll', this.scrollEvent, true);
  }

  ngOnChanges(): void {
    this.disableInput();

    if (this.virtualScroll) {
      this.virtualScroll.scrollToIndex(0);
      this.virtualScroll.checkViewportSize();
    }
  }

  onDropdownOpen() {
    this.virtualScroll.scrollToIndex(0);
    this.virtualScroll.checkViewportSize();
  }

  autocompleteValuesTrackBy(_index: number, option: Option): string {
    return option?.value;
  }

  onDropdownSelect(value: Option): void {
    this.optionChange.emit(value);
    this.input.nativeElement.blur();
    this.formControl.setValue('');
  }

  disableInput(): void {
    if (this.disabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  setHeight(): void {
    this.autocompleteHeight = `${Math.min(this.autocompleteValues.length * 89, 256)}px`;
  }

  setAutocompleteValues(filterText: string): void {
    if (filterText) {
      this.autocompleteValues = this.filterValues(filterText);
    } else {
      this.autocompleteValues = this.options?.slice();
    }
  }

  filterValues(value: string): Option[] {
    const filterValue = value?.toLowerCase().trim();

    return this.options?.filter(
      (option) =>
        option.viewValue?.toLowerCase().indexOf(filterValue) !== -1 ||
        option.viewValueTwo?.toLowerCase().indexOf(filterValue) !== -1 ||
        option.viewValueThree?.toLowerCase().indexOf(filterValue) !== -1
    );
  }

  private scrollEvent = (): void => {
    if (this.autoCompleteTrigger.panelOpen) {
      this.autoCompleteTrigger.updatePosition();
    }
  };
}
